import { usePanelBadges } from "@/lib/panel-badges";
import { SidebarBadge } from "@/components/layout/sidebar-badge";
import React, { ReactNode } from "react";
import { activeNavHref } from "@/lib/active-nav";
import { Link, useLocation } from "wouter";
import {
  Settings,
  Menu,
  X,
  ChevronRight,
  Package,
  BarChart3,
  Home,
  Store,
  Briefcase,
  GraduationCap,
  CreditCard,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { usePanelAccess } from "@/hooks/use-panel-access";
import {
  canAccessProfessionalJobsProfile,
  canAccessTeachingPanel,
  PROFESSIONAL_PANEL_PATH,
  TEACHING_ENTRY_PATH,
} from "@/lib/panel-switcher-access";
import Navbar from "./navbar";

interface SellerPanelLayoutProps {
  children: ReactNode;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const sellerNavItems = [
  { title: "Dashboard", href: "/seller", icon: Home },
  { title: "My Storefront", href: "/seller/storefront", icon: Store },
  { title: "Curriculum Resources", href: "/seller/resources", icon: Package },
  { title: "Sales", href: "/seller/analytics", icon: BarChart3 },
  { title: "Payments", href: "/seller/payments", icon: CreditCard, badge: "earnings" as const },
  { title: "Settings", href: "/seller/settings", icon: Settings },
];

export default function SellerPanelLayout({ children }: SellerPanelLayoutProps) {
  // Default the sidebar to closed on viewports below the lg: breakpoint (1024px).
  // Otherwise on mobile the seller panel opens with a 208px overlay sidebar +
  // black/80 backdrop covering the content, leaving the user unable to interact
  // with the storefront until they manually tap the X.
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const [location, navigate] = useLocation();
  const { data: badges } = usePanelBadges('seller');
  const { user } = useFirebaseAuth();
  const { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus } = usePanelAccess();

  const sub = { isNobility, isRoyalty, isImperial, subscriptionTier, subscriptionStatus };
  const showProfessional = canAccessProfessionalJobsProfile(user, sub);
  const showTeaching = canAccessTeachingPanel(user, sub);
  const hasOtherPanels = showProfessional || showTeaching;

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // Sub-pages count (e.g. /seller/resources/add is Curriculum Resources).
  const activeHref = activeNavHref(
    location,
    sellerNavItems.map((i) => (i.href === "/seller" ? { ...i, exactFor: ["/seller/dashboard"] } : i))
  );
  const isActive = (href: string) => activeHref === href;

  const switcherButtonClass = cn(
    "h-11 w-full border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white",
    "justify-start px-3"
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-900 text-gray-100">
      <Navbar />
      {/* Navbar is `position: fixed` and no longer occupies flow height. This
          shell is a fixed h-screen box, so `margin-top` (not padding) is the
          correct compensation — see instructor-panel-layout.tsx for why. */}
      <div className="flex flex-1 overflow-hidden mt-[var(--app-navbar-height,160px)]">
        <aside
          className={cn(
            "hidden lg:flex flex-col shrink-0 bg-gray-800 border-r border-gray-700 transition-all duration-300 ease-in-out z-50 h-full overflow-hidden",
            isSidebarOpen ? "w-52" : "w-16"
          )}
        >
          <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
            {isSidebarOpen ? (
              <Link href="/seller">
                <button className="flex items-center gap-2 font-semibold hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                  <span>Seller Panel</span>
                </button>
              </Link>
            ) : (
              <div className="w-6"></div>
            )}
            <Button variant="ghost" size="icon" onClick={toggleSidebar}>
              {isSidebarOpen ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <Menu className="h-4 w-4" />
              )}
            </Button>
          </div>

          <nav className="flex-1 grid gap-1 px-2 py-2 content-start">
            {sellerNavItems.map((item) => (
              <Link key={item.href} href={item.href}>
                <Button
                  variant={isActive(item.href) ? "default" : "ghost"}
                  size="sm"
                  className={cn(
                    "h-11 w-full",
                    isSidebarOpen ? "justify-start px-3" : "justify-center px-0",
                    isActive(item.href) && "bg-primary text-primary-foreground font-semibold"
                  )}
                >
                  <span className="relative">
                    <item.icon className="h-5 w-5" />
                    {!isSidebarOpen && "badge" in item && item.badge && <SidebarBadge count={badges?.[item.badge]} dot />}
                  </span>
                  {isSidebarOpen && <span className="ml-2 truncate">{item.title}</span>}
                  {isSidebarOpen && "badge" in item && item.badge && <SidebarBadge count={badges?.[item.badge]} label={item.title} />}
                </Button>
              </Link>
            ))}
          </nav>
          {hasOtherPanels && (
            <div className="border-t border-gray-700 p-2 grid gap-1">
              {showProfessional && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    switcherButtonClass,
                    !isSidebarOpen && "justify-center px-0"
                  )}
                  onClick={() => navigate(PROFESSIONAL_PANEL_PATH)}
                >
                  <Briefcase className="h-4 w-4 shrink-0" />
                  {isSidebarOpen && (
                    <span className="ml-2 truncate text-xs">Professional Panel</span>
                  )}
                </Button>
              )}
              {showTeaching && (
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    switcherButtonClass,
                    !isSidebarOpen && "justify-center px-0"
                  )}
                  onClick={() => navigate(TEACHING_ENTRY_PATH)}
                >
                  <GraduationCap className="h-4 w-4 shrink-0" />
                  {isSidebarOpen && (
                    <span className="ml-2 truncate text-xs">Teaching &amp; courses</span>
                  )}
                </Button>
              )}
            </div>
          )}
          <div className="border-t border-gray-700 p-2">
            {isSidebarOpen && (
              <p className="text-xs text-gray-400 text-center py-1">v{__APP_VERSION__}</p>
            )}
          </div>
        </aside>

        {isSidebarOpen && (
          <>
            <aside className="fixed inset-y-0 left-0 z-[60] flex flex-col bg-gray-800 w-52 lg:hidden">
              <div className="flex h-16 items-center justify-between border-b border-gray-700 px-4">
                <Link href="/seller">
                  <button className="flex items-center gap-2 font-semibold hover:bg-gray-700/50 p-2 rounded-lg transition-colors">
                    <span>Seller Panel</span>
                  </button>
                </Link>
                <Button variant="ghost" size="icon" onClick={toggleSidebar}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <nav className="flex-1 grid gap-1 px-2 py-2 content-start">
                {sellerNavItems.map((item) => (
                  <Link key={`mobile-${item.href}`} href={item.href}>
                    <Button
                      variant={isActive(item.href) ? "default" : "ghost"}
                      className={cn(
                        "h-11 w-full justify-start",
                        isActive(item.href) && "bg-primary text-primary-foreground font-semibold"
                      )}
                      onClick={() => setIsSidebarOpen(false)}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="ml-2">{item.title}</span>
                      {"badge" in item && item.badge && <SidebarBadge count={badges?.[item.badge]} label={item.title} />}
                    </Button>
                  </Link>
                ))}
              </nav>
              {hasOtherPanels && (
                <div className="border-t border-gray-700 p-2 mt-auto grid gap-1">
                  {showProfessional && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={switcherButtonClass}
                      onClick={() => {
                        setIsSidebarOpen(false);
                        navigate(PROFESSIONAL_PANEL_PATH);
                      }}
                    >
                      <Briefcase className="h-4 w-4" />
                      <span className="ml-2 truncate text-xs">Professional Panel</span>
                    </Button>
                  )}
                  {showTeaching && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={switcherButtonClass}
                      onClick={() => {
                        setIsSidebarOpen(false);
                        navigate(TEACHING_ENTRY_PATH);
                      }}
                    >
                      <GraduationCap className="h-4 w-4" />
                      <span className="ml-2 truncate text-xs">Teaching &amp; courses</span>
                    </Button>
                  )}
                </div>
              )}
              <div className="border-t border-gray-700 p-2">
                <p className="text-xs text-gray-400 text-center py-1">v{__APP_VERSION__}</p>
              </div>
            </aside>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/80 lg:hidden"
              onClick={toggleSidebar}
              aria-label="Close seller panel navigation"
            />
          </>
        )}

        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-gray-700 bg-gray-800 px-3 sm:px-4 lg:hidden">
            <Button variant="ghost" size="icon" className="h-11 w-11" onClick={toggleSidebar}>
              <Menu className="h-6 w-6" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Seller Panel</span>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto bg-gray-900 p-3 text-white sm:p-4 lg:p-6">
            {/* No "open this on a desktop" banner. It was the first thing a
                seller saw on a phone -- the product apologising for itself
                above the content -- and it cost a fifth of the screen on the
                one page people check on a phone: whether they got paid. The
                Seller panel screens are laid out for small screens; if one
                stops being usable there, fix that screen rather than warn
                everyone off. */}
            <div className="max-w-7xl mx-auto">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
